import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import Proposals from "@/pages/Proposals";
import ProposalWorkspace from "@/pages/ProposalWorkspace";
import Estimate from "@/pages/Estimate";
import CompanyProfile from "@/pages/CompanyProfile";
import KnowledgeBase from "@/pages/KnowledgeBase";
import Autonomous from "@/pages/Autonomous";
import Pipeline from "@/pages/Pipeline";
import Documents from "@/pages/Documents";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import BidDue from "@/pages/BidDue";
import Analytics from "@/pages/Analytics";
import SavedSearches from "@/pages/SavedSearches";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";
import RFPAnalyzer from "@/pages/RFPAnalyzer";
import Branding from "@/pages/Branding";
import ActivityFeed from "@/pages/ActivityFeed";
import Forecaster from "@/pages/Forecaster";
import Connect from "@/pages/Connect";
import Credits from "@/pages/Credits";
import PricingHistory from "@/pages/PricingHistory";
import WrapRates from "@/pages/WrapRates";
import PastPerformance from "@/pages/PastPerformance";
import BidSkills from "@/pages/BidSkills";
import Submittals from "@/pages/Submittals";
import AdminUsage from "@/pages/AdminUsage";
import AdminRoutesMap from "@/pages/AdminRoutesMap";
import Billing from "@/pages/Billing";
import Browsed from "@/pages/Browsed";
import Takeoffs from "@/pages/Takeoffs";
import TakeoffDetail from "@/pages/TakeoffDetail";
import Integrations from "@/pages/Integrations";
import AppShell from "@/components/AppShell";
import { Toaster } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", ""));
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/login"); return; }
    (async () => {
      try {
        await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard");
      } catch (e) {
        navigate("/login");
      }
    })();
  }, [navigate]);
  return <div className="min-h-screen flex items-center justify-center bg-[#070B14] text-cyan-400 font-mono">Authenticating…</div>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#070B14] text-cyan-400 font-mono text-sm tracking-widest">LOADING</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/opportunities" element={<ProtectedRoute><Opportunities /></ProtectedRoute>} />
      <Route path="/opportunities/:noticeId" element={<ProtectedRoute><OpportunityDetail /></ProtectedRoute>} />
      <Route path="/bid-due" element={<ProtectedRoute><BidDue /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/saved-searches" element={<ProtectedRoute><SavedSearches /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/branding" element={<ProtectedRoute><Branding /></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
      <Route path="/rfp" element={<ProtectedRoute><RFPAnalyzer /></ProtectedRoute>} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
      <Route path="/proposals/:noticeId" element={<ProtectedRoute><ProposalWorkspace /></ProtectedRoute>} />
      <Route path="/opportunities/:noticeId/estimate" element={<ProtectedRoute><Estimate /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><CompanyProfile /></ProtectedRoute>} />
      <Route path="/knowledge" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/autonomous" element={<ProtectedRoute><Autonomous /></ProtectedRoute>} />
      <Route path="/forecaster" element={<ProtectedRoute><Forecaster /></ProtectedRoute>} />
      <Route path="/connect" element={<ProtectedRoute><Connect /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
      <Route path="/pricing-history" element={<ProtectedRoute><PricingHistory /></ProtectedRoute>} />
      <Route path="/wrap-rates" element={<ProtectedRoute><WrapRates /></ProtectedRoute>} />
      <Route path="/takeoffs" element={<ProtectedRoute><Takeoffs /></ProtectedRoute>} />
      <Route path="/takeoffs/:takeoffId" element={<ProtectedRoute><TakeoffDetail /></ProtectedRoute>} />
      <Route path="/past-performance" element={<ProtectedRoute><PastPerformance /></ProtectedRoute>} />
      <Route path="/bid-skills" element={<ProtectedRoute><BidSkills /></ProtectedRoute>} />
      <Route path="/submittals" element={<ProtectedRoute><Submittals /></ProtectedRoute>} />
      <Route path="/admin/usage" element={<ProtectedRoute><AdminUsage /></ProtectedRoute>} />
      <Route path="/admin/routes-map" element={<ProtectedRoute><AdminRoutesMap /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/browsed" element={<ProtectedRoute><Browsed /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
